import apiClient from './api';

/**
 * Onboarding Service
 *
 * API calls for onboarding state management:
 * - Welcome wizard
 * - Onboarding checklist
 * - Interactive tooltips
 */

// Checklist item IDs (must match backend)
export const CHECKLIST_ITEMS = {
  CONNECT_CHANNEL: 'connect_channel',
  CREATE_CALENDAR: 'create_calendar',
  CREATE_POST: 'create_post',
  SCHEDULE_POST: 'schedule_post',
  EXPLORE_CALENDAR: 'explore_calendar',
  SET_UP_CAMPAIGN: 'set_up_campaign',
  INVITE_TEAM_MEMBER: 'invite_team_member',
  CONNECT_ANOTHER_CHANNEL: 'connect_another_channel',
} as const;

export const CLIENT_CHECKLIST_ITEMS = {
  EXPLORE_DASHBOARD: 'explore_dashboard',
  BROWSE_CALENDAR: 'browse_calendar',
  REVIEW_APPROVAL_POST: 'review_approval_post',
  APPROVE_POST: 'approve_post',
  LEAVE_COMMENT: 'leave_comment',
  CREATE_DRAFT: 'create_draft',
} as const;

export const AGENCY_CHECKLIST_ITEMS = {
  CREATE_CLIENT_WORKSPACE: 'create_client_workspace',
  INVITE_STAFF_MEMBER: 'invite_staff_member',
  SETUP_BRANDING: 'setup_branding',
  CONNECT_CLIENT_CHANNEL: 'connect_client_channel',
  CREATE_CLIENT_CALENDAR: 'create_client_calendar',
  CUSTOMIZE_AGENCY_PROFILE: 'customize_agency_profile',
} as const;

export type ChecklistItemId = typeof CHECKLIST_ITEMS[keyof typeof CHECKLIST_ITEMS];
export type ClientChecklistItemId = typeof CLIENT_CHECKLIST_ITEMS[keyof typeof CLIENT_CHECKLIST_ITEMS];
export type AgencyChecklistItemId = typeof AGENCY_CHECKLIST_ITEMS[keyof typeof AGENCY_CHECKLIST_ITEMS];
export type AnyChecklistItemId = ChecklistItemId | ClientChecklistItemId | AgencyChecklistItemId;
export type OnboardingUserType = 'individual' | 'team' | 'agency';
export type OnboardingVariant = 'regular' | 'client' | 'agency';

export interface OnboardingProgress {
  id: number;
  userId: number;
  teamId: number | null;
  teamType: OnboardingUserType | null;
  variant: OnboardingVariant;
  agencyId: number | null;
  wizardCompleted: boolean;
  wizardCompletedAt: string | null;
  wizardSkipped: boolean;
  wizardCurrentStep: number;
  checklistItems: Record<string, boolean>;
  checklistDismissed: boolean;
  checklistDismissedAt: string | null;
  seenTooltips: string[];
  tooltipsDisabled: boolean;
  showWizard: boolean;
  showChecklist: boolean;
  checklistProgress: number;
  isAgencyClient: boolean;
  createdAt: string;
  updatedAt: string;
}

export const onboardingService = {
  /**
   * Get current user's onboarding progress
   */
  async getProgress(): Promise<OnboardingProgress> {
    const response = await apiClient.get<{ success: boolean; data: OnboardingProgress }>(
      '/api/onboarding'
    );
    return response.data.data;
  },

  /**
   * Complete the welcome wizard
   */
  async completeWizard(): Promise<void> {
    await apiClient.post('/api/onboarding/wizard/complete');
  },

  /**
   * Skip the welcome wizard
   */
  async skipWizard(): Promise<void> {
    await apiClient.post('/api/onboarding/wizard/skip');
  },

  /**
   * Update current wizard step
   */
  async updateWizardStep(step: number): Promise<void> {
    await apiClient.patch('/api/onboarding/wizard/step', { step });
  },

  /**
   * Set team type (individual, team, agency)
   */
  async setTeamType(teamType: OnboardingUserType): Promise<void> {
    await apiClient.patch('/api/onboarding/team-type', { teamType });
  },

  /**
   * Mark checklist item as completed
   */
  async completeChecklistItem(item: AnyChecklistItemId): Promise<void> {
    await apiClient.put(`/api/onboarding/checklist/${item}`);
  },

  /**
   * Dismiss the onboarding checklist
   */
  async dismissChecklist(): Promise<void> {
    await apiClient.post('/api/onboarding/checklist/dismiss');
  },

  /**
   * Mark tooltip as seen
   */
  async markTooltipSeen(tooltipId: string): Promise<void> {
    await apiClient.post(`/api/onboarding/tooltip/${tooltipId}/seen`);
  },

  /**
   * Disable all tooltips
   */
  async disableTooltips(): Promise<void> {
    await apiClient.post('/api/onboarding/tooltips/disable');
  },
};
